import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Text } from '@paygreen/paygreen-ui';
import { useAPIError, useMode } from '../../../context';
import { frLangLocale } from '../../../utils/constants';
import ProductModal from '../shared/ProductModal';
import {
    patchMatchingProduct,
    getOneEmissionFactor,
    patchMatchingCategory,
} from '../service';
import AddManuallyProductForm from '../addManuallyProduct/AddManuallyProductForm';
import DeleteProductText from './DeleteProductText';

const ModifyProduct = ({
    t,
    i18n,
    handleRefreshData,
    setOpen,
    isOpen,
    productData,
    mode: actionMode,
    openMatching,
    isCategory,
}) => {
    const { addError } = useAPIError();
    const { mode } = useMode();
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDetailsLoading, setIsDetailsLoading] = useState(false);
    const [displayCategoryForm, setDisplayCategoryForm] =
        useState(openMatching);

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const emissionFactorName = frLangLocale.includes(i18n.language)
        ? productData?.emissionFactorFullNameFrench
        : productData?.emissionFactorFullNameEnglish;

    const monetaryRatioName = frLangLocale.includes(i18n.language)
        ? productData?.monetaryRatioNameFrench
        : productData?.monetaryRatioNameEnglish;

    const emissionFactorCategoryName = frLangLocale.includes(i18n.language)
        ? productData?.emissionFactorCategoryNameFrench
        : productData?.emissionFactorCategoryNameEnglish;

    const monetaryRatioCategoryName = frLangLocale.includes(i18n.language)
        ? productData?.monetaryRatioCategoryNameFrench
        : productData?.monetaryRatioCategoryNameEnglish;

    // initializing all react-hook-form values and methods
    const defaultValues = {
        categoryName: productData?.categoryName,
        productExternalReference: productData?.productExternalReference,
        productName: productData?.productName,
        emissionExternalId: productData?.emissionExternalId,
        monetaryRatioMatch: productData?.monetaryRatioMatch,
        emissionFactorSource: productData?.emissionFactorSource,
        emissionFactorName,
        emissionFactorCategoryName,
        monetaryRatioName,
        monetaryRatioCategoryName,
        emissionFactorValue:
            productData?.emissionFactorValue + productData?.emissionFactorUnit,
        monetaryRatioValue:
            productData?.monetaryRatioValue + productData?.monetaryRatioUnit,
        documentation: '',
        source: '',
    };

    const methods = useForm({
        defaultValues: defaultValues,
        mode: 'onTouched',
        reValidateMode: 'onChange',
    });

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        getValues,
        reset,
        clearErrors,
    } = methods;

    const productId = productData?.productExternalReference || '';
    const productName = productData?.productName || '';
    const productEmissionExternalId = productData?.emissionExternalId;
    const productMonetaryRatioMatch = productData?.monetaryRatioMatch;

    // watch specifically for emissionExternalId field without rerendering all form
    const selectedProduct =
        watch('emissionExternalId') || watch('monetaryRatioMatch')?.toString();

    const clearInputs = useCallback(() => {
        for (const [key] of Object.entries(defaultValues)) {
            setValue(key, '');
        }
    }, [defaultValues, setValue]);

    // Watch for changes on inputs
    useEffect(() => {
        register({ name: 'categoryName' });
        register({ name: 'productExternalReference' });
        register({ name: 'productName' });
        register({ name: 'emissionExternalId' });
        register({ name: 'monetaryRatioMatch' });
        register({ name: 'emissionFactorName' });
        register({ name: 'monetaryRatioName' });
        register({ name: 'emissionFactorValue' });
        register({ name: 'monetaryRatioValue' });
        register({ name: 'documentation' });
        register({ name: 'source' });
    }, [register]);

    useEffect(() => {
        setDisplayCategoryForm(openMatching);
    }, [openMatching, isOpen]);

    const cancelCloseModal = useCallback(() => {
        setOpen(false);

        // we need the reset methods to wait until modal is closed
        setTimeout(() => {
            setDisplayCategoryForm(false);
            setErrorMessage();
            clearErrors();

            clearInputs();
        }, 200);
    }, [setOpen, clearErrors, clearInputs]);

    useEffect(() => {
        reset({
            categoryName: productData?.categoryName,
            productExternalReference: productData?.productExternalReference,
            productName: productData?.productName,
            emissionExternalId: productData?.emissionExternalId,
            monetaryRatioMatch: productData?.monetaryRatioMatch,
            emissionFactorSource: productData?.emissionFactorSource,
            emissionFactorName,
            monetaryRatioName,
            emissionFactorValue:
                productData?.emissionFactorValue +
                productData?.emissionFactorUnit,
            monetaryRatioValue:
                productData?.monetaryRatioValue +
                productData?.monetaryRatioUnit,
            documentation: '',
            source: '',
        });
    }, [productData, emissionFactorName, monetaryRatioName, reset]);

    /**
     * Patch request to API to "delete" (switch to archived) a product
     */
    const deleteMatchingProduct = () => {
        setIsLoading(true);
        setErrorMessage(null);

        const archivedProduct = {
            isArchived: 1,
        };

        patchMatchingProduct(productId, archivedProduct, mode)
            .then(() => {
                handleRefreshData(true);
                setOpen(false);

                setTimeout(() => {
                    clearInputs();
                }, 200);

                !isLoading &&
                    toast(
                        <Text colorPallet="theme" textSize="xs">
                            <Trans i18nKey="form.toastSuccess.deleteProduct">
                                ...
                                <Text
                                    htmlTag="strong"
                                    colorPallet="theme"
                                    textSize="xs"
                                >
                                    {{
                                        productName: productName,
                                    }}
                                </Text>
                                ...
                            </Trans>
                        </Text>,
                    );
            })
            .catch((error) => {
                const { detail, status } = error?.response?.data;

                setErrorMessage(status + ' / ' + detail);
            })
            .finally(() => {
                isMounted.current && setIsLoading(false);
            });
    };

    /**
     * Patch request to API to update a product
     * @param {object} updatedObject - object with productName or emissionExternalId modified
     */
    const updateMatchingProduct = (updatedObject) => {
        setIsLoading(true);

        if (isCategory) {
            return patchMatchingCategory(updatedObject, mode)
                .then(() => {
                    handleRefreshData();
                    setOpen(false);

                    setTimeout(() => {
                        clearInputs();
                    }, 200);

                    !isLoading &&
                        toast(
                            <Text colorPallet="theme" textSize="xs">
                                {updatedObject.monetaryRatioMatch && (
                                    <Trans i18nKey="form.toastSuccess.associateCategorySector">
                                        ...
                                        <Text
                                            htmlTag="strong"
                                            colorPallet="theme"
                                            textSize="xs"
                                        >
                                            {{
                                                categoryName:
                                                    updatedObject.externalProductCategory,
                                                matchingName:
                                                    getValues()
                                                        ?.monetaryRatioName,
                                            }}
                                        </Text>
                                        ...
                                    </Trans>
                                )}
                                {updatedObject.emissionExternalId && (
                                    <Trans i18nKey="form.toastSuccess.associateCategoryEquivalence">
                                        ...
                                        <Text
                                            htmlTag="strong"
                                            colorPallet="theme"
                                            textSize="xs"
                                        >
                                            {{
                                                categoryName:
                                                    updatedObject.externalProductCategory,
                                                matchingName:
                                                    getValues()
                                                        ?.emissionFactorName,
                                            }}
                                        </Text>
                                        ...
                                    </Trans>
                                )}
                            </Text>,
                        );
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;

                    setErrorMessage(status + ' / ' + detail);
                })
                .finally(() => {
                    isMounted.current && setIsLoading(false);
                });
        }

        patchMatchingProduct(productId, updatedObject, mode)
            .then(() => {
                handleRefreshData();
                setOpen(false);

                setTimeout(() => {
                    clearInputs();
                }, 200);

                !isLoading &&
                    toast(
                        <Text colorPallet="theme" textSize="xs">
                            <Trans i18nKey="form.toastSuccess.modifyProduct">
                                ...
                                <Text
                                    htmlTag="strong"
                                    colorPallet="theme"
                                    textSize="xs"
                                >
                                    {{
                                        productName: productId,
                                    }}
                                </Text>
                                ...
                            </Trans>
                        </Text>,
                    );
            })
            .catch((error) => {
                const { detail, status } = error?.response?.data;

                setErrorMessage(status + ' / ' + detail);
            })
            .finally(() => {
                isMounted.current && setIsLoading(false);
            });
    };

    /**
     * Get request to API to get missing emission factor details and update default values
     */
    const getEmissionFactorDetails = useCallback(() => {
        setIsDetailsLoading(true);

        getOneEmissionFactor(productEmissionExternalId, mode)
            .then((res) => {
                isMounted.current &&
                    reset({
                        source: res.data.source,
                        documentation: res.data.documentation,
                        emissionExternalId: res.data.emissionExternalId,
                    });
            })
            .catch((error) =>
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                ),
            )
            .finally(() => {
                isMounted.current && setIsDetailsLoading(false);
            });
    }, [addError, productEmissionExternalId, reset, mode]);

    /**
     * ToDO when source will be different of ADEME, Get request to API to get missing monetary ratio details and update default values
     */
    const getMonetaryRatioDetails = useCallback(() => {
        setIsDetailsLoading(true);

        isMounted.current &&
            reset({
                source: 'ADEME',
                documentation: 'https://www.ademe.fr/',
                monetaryRatioMatch: productMonetaryRatioMatch,
            });
        isMounted.current && setIsDetailsLoading(false);
    }, [productMonetaryRatioMatch, reset]);

    const submitForm = (data) => {
        setErrorMessage(null);
        let updatedProduct = {};
        if (isCategory) {
            updatedProduct = {
                externalProductCategory: data.categoryName,
                emissionExternalId: data.emissionExternalId,
                monetaryRatioMatch: data.monetaryRatioMatch,
            };
        } else {
            updatedProduct = {
                productName: data.productName,
                emissionExternalId: data.emissionExternalId,
                monetaryRatioMatch: data.monetaryRatioMatch,
            };
        }

        updateMatchingProduct(updatedProduct);
    };

    useEffect(() => {
        isMounted.current = true;

        // we get details only if product is linked and when modal is opened
        if (isOpen && !!productEmissionExternalId) {
            getEmissionFactorDetails();
        }
        if (isOpen && !!productMonetaryRatioMatch) {
            getMonetaryRatioDetails();
        }
        return () => {
            isMounted.current = false;
        };
    }, [
        getEmissionFactorDetails,
        getMonetaryRatioDetails,
        isOpen,
        productEmissionExternalId,
        productMonetaryRatioMatch,
    ]);

    return (
        <FormProvider {...methods}>
            <ProductModal
                modalTitle={
                    isCategory
                        ? t('categories.modal.title')
                        : actionMode === 'edit' && displayCategoryForm
                        ? t('matching.addManuallyForm.modalEquivalentTitle')
                        : actionMode === 'edit'
                        ? t('matching.updateForm.modalTitle')
                        : t('matching.deleteModal.modalTitle')
                }
                blockWidth={displayCategoryForm ? 'xl' : 'md'}
                isOpen={isOpen}
                isLoading={isLoading || isDetailsLoading}
                cancelCloseModal={cancelCloseModal}
                deleteProduct={deleteMatchingProduct}
                errorMessage={errorMessage}
                mode={actionMode}
                hasDisabledMode={displayCategoryForm || isDetailsLoading}
            >
                {actionMode === 'edit' ? (
                    <AddManuallyProductForm
                        onSubmit={handleSubmit(submitForm)}
                        defaultValues={defaultValues}
                        isOpen={isOpen}
                        displayCategoryForm={displayCategoryForm}
                        selectedProduct={selectedProduct}
                        cancelCloseModal={cancelCloseModal}
                        isCategory={isCategory}
                    />
                ) : (
                    <DeleteProductText
                        productName={productData?.productName || ''}
                    />
                )}
            </ProductModal>
        </FormProvider>
    );
};

ModifyProduct.propTypes = {
    handleRefreshData: PropTypes.func,
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    productData: PropTypes.object,
    mode: PropTypes.string,
    openMatching: PropTypes.bool,
    isCategory: PropTypes.bool,
};

ModifyProduct.defaultProps = {
    isOpen: false,
    mode: 'edit',
    openMatching: false,
    isCategory: false,
};

export default withTranslation('common')(ModifyProduct);
